/* src/components/navbar/navbar-client.tsx */

"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLinks } from "./nav-links";
import { ToggleLang } from "../shared/toggle-lang";

// 定义从服务器组件接收的 props 类型
interface NavItem {
	href: string;
	text: string;
}

interface NavbarClientProps {
	navItems: NavItem[];
	logoText: string;
}

export function NavbarClient({ navItems, logoText }: NavbarClientProps) {
	const [isOpen, setIsOpen] = useState(false);

	const menuVariants = {
		hidden: { opacity: 0, y: -20 },
		visible: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: -20 },
	};

	return (
		<nav className="navbar color w-full h-20 flex items-center justify-between px-6 sm:px-12">
			{/* 左侧 Logo 和公司名链接 (始终可见) */}
			<Link href="/" className="flex items-center space-x-4">
				<Image
					src="/favicon-96x96.png"
					alt="Logo"
					width={60}
					height={60}
					className="rounded-md"
				/>
				<span className="text-3xl font-semibold text-[var(--color-text)]">
					{logoText}
				</span>
			</Link>

			{/* 桌面端导航 (中等及以上屏幕可见) */}
			<div className="hidden md:flex items-center gap-x-8">
				<NavLinks navItems={navItems} />
				<ToggleLang />
			</div>

			{/* 移动端汉堡菜单按钮 (仅在小屏幕可见) */}
			<div className="md:hidden">
				<button
					onClick={() => setIsOpen(!isOpen)}
					aria-label="Toggle navigation menu"
					className="z-50 relative"
				>
					{isOpen ? (
						<X className="w-8 h-8 text-gray-800" />
					) : (
						<Menu className="w-8 h-8 text-[var(--color-text)]" />
					)}
				</button>
			</div>

			{/* 移动端展开菜单 (全屏覆盖) */}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						key="mobile-menu"
						variants={menuVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						transition={{ duration: 0.2 }}
						className="fixed inset-0 z-40 flex flex-col items-center justify-center space-y-10 bg-white md:hidden"
					>
						{navItems.map(({ href, text }) => (
							<Link
								key={href}
								href={href}
								onClick={() => setIsOpen(false)} // 点击链接后关闭菜单
								className="text-3xl font-semibold text-gray-800 hover:text-gray-600"
							>
								{text}
							</Link>
						))}
						<div className="pt-8">
							<ToggleLang />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</nav>
	);
}
